/** Resolves the effective charge basis, falling back to the legacy pricingMode. */
export function resolveChargeBasis(fee) {
    if (fee.chargeBasis === 'per_program')
        return 'per_year';
    if (fee.chargeBasis)
        return fee.chargeBasis;
    return fee.pricingMode === 'per-credit' ? 'per_credit' : 'per_term';
}
/**
 * The year a term is billed in under per_year. Two terms with the same key
 * share ONE annual plan. Dates are `YYYY-MM-DD`.
 *
 * - calendar → `cal-2026`, the year the term starts in.
 * - program  → `prog-N`: full months from the intake term's start to this
 *   term's start, ÷ 12, + 1. Unknown intake, or a term before it → year 1.
 */
export function annualBillingKey(basis, termStartYmd, intakeStartYmd) {
    if (basis === 'calendar')
        return `cal-${termStartYmd.slice(0, 4)}`;
    if (!intakeStartYmd)
        return 'prog-1';
    const [ty, tm, td] = termStartYmd.split('-').map(Number);
    const [iy, im, id] = intakeStartYmd.split('-').map(Number);
    const fullMonths = (ty - iy) * 12 + (tm - im) - (td < id ? 1 : 0);
    return `prog-${Math.max(0, Math.floor(fullMonths / 12)) + 1}`;
}
/**
 * Computes the COURSES tuition (excludes the one-time enrollmentFee) for a
 * selection, from the program's fee config. Single source of truth shared by
 * the enrollment callable (authoritative charge) and the portal catalog
 * (display) so the shown total matches what is billed.
 */
export function computeCoursesFee(fee, selection) {
    switch (resolveChargeBasis(fee)) {
        case 'per_credit':
            return selection.totalCredits * (fee.pricePerCredit ?? 0);
        case 'per_course':
            return selection.courseCount * (fee.ratePerCourse ?? 0);
        case 'per_year':
        case 'per_term':
        default:
            return fee.programTotalFee ?? 0; // flat
    }
}
export class TuitionPlan {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        if (params.installments.length === 0) {
            throw new Error('A tuition plan must have at least one installment');
        }
        const now = new Date();
        return new TuitionPlan({
            ...params,
            admissionId: params.admissionId ?? null,
            status: 'active',
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new TuitionPlan(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get studentId() {
        return this.props.studentId;
    }
    get admissionId() {
        return this.props.admissionId;
    }
    get programId() {
        return this.props.programId;
    }
    get termId() {
        return this.props.termId;
    }
    get currency() {
        return this.props.currency;
    }
    get installments() {
        return [...this.props.installments];
    }
    get paymentPlanOption() {
        return this.props.paymentPlanOption;
    }
    get status() {
        return this.props.status;
    }
    get pricingMode() {
        return this.props.pricingMode;
    }
    get frozenTotalFee() {
        return this.props.frozenTotalFee;
    }
    get frozenEnrollmentFee() {
        return this.props.frozenEnrollmentFee;
    }
    get totalAmount() {
        return this.props.installments.reduce((sum, i) => sum + i.amount, 0);
    }
    /** Replace installments (e.g. when student reorganizes their payment schedule).
     *  Total must remain the same unless allowTotalChange is true. */
    updateInstallments(installments, allowTotalChange = false) {
        if (installments.length === 0) {
            throw new Error('Plan must have at least one installment');
        }
        const newTotal = installments.reduce((s, i) => s + i.amount, 0);
        const currentTotal = this.totalAmount;
        if (!allowTotalChange && Math.abs(newTotal - currentTotal) > 1) {
            // allow 1 CLP rounding diff
            throw new Error(`Total amount must remain ${currentTotal}. Got ${newTotal}`);
        }
        return new TuitionPlan({
            ...this.props,
            installments,
            ...(allowTotalChange ? { frozenTotalFee: newTotal } : {}),
            updatedAt: new Date(),
        });
    }
    /**
     * Append an extra installment for a per-credit course added after plan creation.
     * Unlike updateInstallments, this deliberately changes the total.
     */
    addInstallment(installment) {
        if (this.props.pricingMode !== 'per-credit') {
            throw new Error('addInstallment is only allowed on per-credit plans');
        }
        return new TuitionPlan({
            ...this.props,
            installments: [...this.props.installments, installment],
            frozenTotalFee: this.props.frozenTotalFee + installment.amount,
            updatedAt: new Date(),
        });
    }
    /**
     * Spread extra amount across unpaid monthly installments.
     * Paid installments (identified by paidInstallmentIds) are never modified.
     * Used when a student adds a course and prefers to redistribute over remaining months.
     */
    redistributeUnpaidInstallments(extraAmount, paidInstallmentIds) {
        if (this.props.pricingMode !== 'per-credit') {
            throw new Error('redistributeUnpaidInstallments is only allowed on per-credit plans');
        }
        const paidSet = new Set(paidInstallmentIds);
        const unpaid = this.props.installments.filter((i) => i.type === 'monthly' && !paidSet.has(i.id));
        if (unpaid.length === 0) {
            throw new Error('No unpaid monthly installments to redistribute into');
        }
        const baseExtra = Math.floor(extraAmount / unpaid.length);
        const remainder = extraAmount - baseExtra * unpaid.length;
        const updatedInstallments = this.props.installments.map((inst, _idx) => {
            if (inst.type !== 'monthly' || paidSet.has(inst.id))
                return inst;
            const isLastUnpaid = inst.id === unpaid[unpaid.length - 1].id;
            return {
                ...inst,
                amount: inst.amount + baseExtra + (isLastUnpaid ? remainder : 0),
            };
        });
        return new TuitionPlan({
            ...this.props,
            installments: updatedInstallments,
            frozenTotalFee: this.props.frozenTotalFee + extraAmount,
            updatedAt: new Date(),
        });
    }
    markCompleted() {
        return new TuitionPlan({ ...this.props, status: 'completed', updatedAt: new Date() });
    }
    /**
     * Withdrawal — policy defaults + per-installment admin overrides.
     *
     * Default policy (applied when no override provided for an installment):
     *   - Enrollment fee (matrícula): retained at original amount.
     *   - Monthly with `dueDate <= withdrawalDate`: retained at original amount.
     *   - Monthly with `dueDate > withdrawalDate`: retained at 0 (zeroed).
     *
     * Admin override: `retentionOverrides[installmentId] = retainedAmount`
     *   - Must satisfy `0 <= retainedAmount <= originalAmount`.
     *   - Replaces the policy default for that installment.
     *   - Use 0 to forgive an outstanding debt (e.g. board waives a missed month).
     *   - Use a partial value to keep some retention while refunding the rest.
     *
     * Refund per installment is computed from the paid map:
     *   `refund = max(0, paidByInstallment[id] - retainedAmount)`
     *
     * Final installment amount is set to `retainedAmount`. `frozenTotalFee` is
     * recalculated to the total retained so financial projections stay coherent.
     * Status flips to 'withdrawn'.
     */
    markWithdrawn(withdrawalDate, paidByInstallment, retentionOverrides) {
        const y = withdrawalDate.getUTCFullYear();
        const m = String(withdrawalDate.getUTCMonth() + 1).padStart(2, '0');
        const d = String(withdrawalDate.getUTCDate()).padStart(2, '0');
        const cutoff = `${y}-${m}-${d}`;
        const overrides = retentionOverrides ?? {};
        const zeroedIds = [];
        let refundAmount = 0;
        const updatedInstallments = this.props.installments.map((inst) => {
            let retained;
            if (Object.prototype.hasOwnProperty.call(overrides, inst.id)) {
                retained = overrides[inst.id] ?? 0;
                if (retained < 0 || retained > inst.amount) {
                    throw new Error(`Retention override for ${inst.id} must be between 0 and ${inst.amount}`);
                }
            }
            else if (inst.type === 'enrollment') {
                retained = inst.amount;
            }
            else if (inst.dueDate <= cutoff) {
                retained = inst.amount;
            }
            else {
                retained = 0;
            }
            const paid = paidByInstallment.get(inst.id) ?? 0;
            const refundForInst = Math.max(0, paid - retained);
            refundAmount += refundForInst;
            if (retained === 0)
                zeroedIds.push(inst.id);
            return { ...inst, amount: retained };
        });
        const retainedAmount = updatedInstallments.reduce((s, i) => s + i.amount, 0);
        return {
            plan: new TuitionPlan({
                ...this.props,
                installments: updatedInstallments,
                frozenTotalFee: retainedAmount,
                status: 'withdrawn',
                updatedAt: new Date(),
            }),
            zeroedInstallmentIds: zeroedIds,
            retainedAmount,
            refundAmount,
        };
    }
    markDefaulted() {
        return new TuitionPlan({ ...this.props, status: 'defaulted', updatedAt: new Date() });
    }
    /**
     * Apply a tuition exemption for worker-students.
     * Zeroes all installments and frozen totals, marks the plan as 'completed',
     * and records who applied it and why for audit purposes.
     */
    applyExemption(adminUid, reason) {
        if (!reason.trim())
            throw new Error('Exemption reason is required');
        if (this.props.status !== 'active') {
            throw new Error('Exemption can only be applied to active plans');
        }
        const now = new Date();
        const zeroedInstallments = this.props.installments.map((inst) => ({
            ...inst,
            amount: 0,
        }));
        return new TuitionPlan({
            ...this.props,
            installments: zeroedInstallments,
            frozenTotalFee: 0,
            frozenEnrollmentFee: 0,
            status: 'completed',
            exemptionReason: reason.trim(),
            exemptedBy: adminUid,
            exemptedAt: now.toISOString(),
            updatedAt: now,
        });
    }
    get isExempt() {
        return !!this.props.exemptionReason;
    }
    get exemptionReason() {
        return this.props.exemptionReason;
    }
    get exemptedBy() {
        return this.props.exemptedBy;
    }
    get exemptedAt() {
        return this.props.exemptedAt;
    }
    get discountAmount() {
        return this.props.discountAmount;
    }
    get discountReason() {
        return this.props.discountReason;
    }
    get discountedBy() {
        return this.props.discountedBy;
    }
    get discountedAt() {
        return this.props.discountedAt;
    }
    get adminScholarshipGrant() {
        return this.props.adminScholarshipGrant;
    }
    /**
     * Apply a discount subtracting `amount` from the `frozenTotalFee`
     * and proportionally distributing the subtraction among the unpaid `monthly` installments.
     */
    applyDiscount(adminUid, reason, amount, paidInstallmentIds) {
        if (!reason.trim())
            throw new Error('Discount reason is required');
        if (amount <= 0)
            throw new Error('Discount amount must be positive');
        if (this.props.status !== 'active') {
            throw new Error('Discount can only be applied to active plans');
        }
        const paidSet = new Set(paidInstallmentIds);
        const unpaid = this.props.installments.filter((i) => i.type === 'monthly' && !paidSet.has(i.id));
        if (unpaid.length === 0) {
            throw new Error('No unpaid monthly installments to apply discount into');
        }
        const currentUnpaidTotal = unpaid.reduce((s, i) => s + i.amount, 0);
        // Cap the discount so we don't end up with negative totals
        const effectiveDiscount = Math.min(amount, currentUnpaidTotal);
        const baseDiscount = Math.floor(effectiveDiscount / unpaid.length);
        const remainder = effectiveDiscount - baseDiscount * unpaid.length;
        const updatedInstallments = this.props.installments.map((inst) => {
            if (inst.type !== 'monthly' || paidSet.has(inst.id))
                return inst;
            const isLastUnpaid = inst.id === unpaid[unpaid.length - 1].id;
            return {
                ...inst,
                amount: Math.max(0, inst.amount - baseDiscount - (isLastUnpaid ? remainder : 0)),
            };
        });
        const now = new Date();
        return new TuitionPlan({
            ...this.props,
            installments: updatedInstallments,
            frozenTotalFee: Math.max(0, this.props.frozenTotalFee - effectiveDiscount),
            discountAmount: (this.props.discountAmount || 0) + effectiveDiscount,
            discountReason: reason.trim(),
            discountedBy: adminUid,
            discountedAt: now.toISOString(),
            updatedAt: now,
        });
    }
    /**
     * Apply an administrative scholarship grant (beca ad-hoc) to the plan.
     *
     * - Applies independent discount percentages to the enrollment (matrícula) and
     *   monthly (arancel) installments.
     * - Already-paid installments (identified by paidInstallmentIds) are NEVER modified.
     * - If the enrollment installment is already paid, enrollmentDiscountPercent is silently
     *   ignored (no retroactive credit is generated).
     * - Can only be applied to active plans.
     * - Can only be applied once. Throws if a grant already exists.
     */
    applyAdminScholarship(params) {
        const source = params.source ?? 'manual';
        if (this.props.status !== 'active') {
            throw new Error('Admin scholarship can only be applied to active plans');
        }
        const existing = this.props.adminScholarshipGrant;
        if (existing) {
            // Idempotent: the same scholarship source+ref is already applied → no-op.
            if (source !== 'manual' &&
                (existing.source ?? 'manual') === source &&
                existing.sourceRef === params.sourceRef) {
                return this;
            }
            // Any other existing grant blocks (a manual grant must be revoked before a
            // scholarship applies, and vice-versa). Machine reason for the caller to map.
            throw new Error('scholarship.grant_conflict');
        }
        if (params.enrollmentDiscountPercent < 0 || params.enrollmentDiscountPercent > 100) {
            throw new Error('enrollmentDiscountPercent must be between 0 and 100');
        }
        if (params.tuitionDiscountPercent < 0 || params.tuitionDiscountPercent > 100) {
            throw new Error('tuitionDiscountPercent must be between 0 and 100');
        }
        if (!params.reason.trim()) {
            throw new Error('Scholarship grant reason is required');
        }
        const paidSet = new Set(params.paidInstallmentIds);
        const now = new Date();
        // ── Enrollment discount ────────────────────────────────────────────────
        // Only applied if the enrollment installment has NOT been paid.
        let enrollmentDiscountAmount = 0;
        const enrollmentInstallment = this.props.installments.find((i) => i.id === 'enrollment');
        const enrollmentAlreadyPaid = enrollmentInstallment ? paidSet.has('enrollment') : true;
        if (!enrollmentAlreadyPaid && params.enrollmentDiscountPercent > 0 && enrollmentInstallment) {
            enrollmentDiscountAmount = Math.round(enrollmentInstallment.amount * (params.enrollmentDiscountPercent / 100));
        }
        // ── Tuition (arancel) discount ─────────────────────────────────────────
        // Distributed proportionally among unpaid monthly installments.
        const unpaidMonthly = this.props.installments.filter((i) => i.type === 'monthly' && !paidSet.has(i.id));
        const unpaidMonthlyTotal = unpaidMonthly.reduce((s, i) => s + i.amount, 0);
        const tuitionDiscountAmount = params.tuitionDiscountPercent > 0
            ? Math.min(Math.round(unpaidMonthlyTotal * (params.tuitionDiscountPercent / 100)), unpaidMonthlyTotal)
            : 0;
        // Distribute tuition discount across unpaid monthly installments proportionally
        let remainingTuitionDiscount = tuitionDiscountAmount;
        const updatedInstallments = this.props.installments.map((inst) => {
            // Enrollment discount
            if (inst.id === 'enrollment' && !enrollmentAlreadyPaid && enrollmentDiscountAmount > 0) {
                return { ...inst, amount: Math.max(0, inst.amount - enrollmentDiscountAmount) };
            }
            // Monthly discount — skip paid
            if (inst.type === 'monthly' && !paidSet.has(inst.id) && tuitionDiscountAmount > 0) {
                const isLast = inst.id === unpaidMonthly[unpaidMonthly.length - 1].id;
                if (isLast) {
                    // Last gets the remaining to avoid rounding drift
                    const discount = remainingTuitionDiscount;
                    remainingTuitionDiscount = 0;
                    return { ...inst, amount: Math.max(0, inst.amount - discount) };
                }
                // Proportional share: (inst.amount / unpaidMonthlyTotal) * tuitionDiscountAmount
                const proportionalDiscount = unpaidMonthlyTotal > 0
                    ? Math.floor((inst.amount / unpaidMonthlyTotal) * tuitionDiscountAmount)
                    : 0;
                remainingTuitionDiscount -= proportionalDiscount;
                return { ...inst, amount: Math.max(0, inst.amount - proportionalDiscount) };
            }
            return inst;
        });
        return new TuitionPlan({
            ...this.props,
            installments: updatedInstallments,
            frozenEnrollmentFee: Math.max(0, this.props.frozenEnrollmentFee - enrollmentDiscountAmount),
            frozenTotalFee: Math.max(0, this.props.frozenTotalFee - enrollmentDiscountAmount - tuitionDiscountAmount),
            adminScholarshipGrant: {
                enrollmentDiscountPercent: params.enrollmentDiscountPercent,
                tuitionDiscountPercent: params.tuitionDiscountPercent,
                enrollmentDiscountAmount,
                tuitionDiscountAmount,
                reason: params.reason.trim(),
                grantedBy: params.adminUid,
                grantedAt: now.toISOString(),
                source,
                ...(params.sourceRef ? { sourceRef: params.sourceRef } : {}),
            },
            updatedAt: now,
        });
    }
    toSnapshot() {
        return { ...this.props };
    }
}
export class TuitionPayment {
    props;
    constructor(props) {
        this.props = props;
    }
    static create(params) {
        if (params.amount <= 0)
            throw new Error('Payment amount must be positive');
        if (!params.reference.trim())
            throw new Error('Payment reference is required');
        const allocTotal = params.allocations.reduce((s, a) => s + a.amount, 0);
        if (Math.abs(allocTotal - params.amount) > 1) {
            throw new Error('Allocations must sum to the payment amount');
        }
        const now = new Date();
        return new TuitionPayment({
            ...params,
            status: 'pending_verification',
            createdAt: now,
            updatedAt: now,
        });
    }
    static reconstitute(props) {
        return new TuitionPayment(props);
    }
    get id() {
        return this.props.id;
    }
    get tenantId() {
        return this.props.tenantId;
    }
    get tuitionPlanId() {
        return this.props.tuitionPlanId;
    }
    get studentId() {
        return this.props.studentId;
    }
    get amount() {
        return this.props.amount;
    }
    get currency() {
        return this.props.currency;
    }
    get paymentDate() {
        return this.props.paymentDate;
    }
    get method() {
        return this.props.method;
    }
    get reference() {
        return this.props.reference;
    }
    get receiptUrl() {
        return this.props.receiptUrl;
    }
    get allocations() {
        return [...this.props.allocations];
    }
    get status() {
        return this.props.status;
    }
    get verifiedBy() {
        return this.props.verifiedBy;
    }
    get verifiedAt() {
        return this.props.verifiedAt;
    }
    get rejectionReason() {
        return this.props.rejectionReason;
    }
    isPendingVerification() {
        return this.props.status === 'pending_verification';
    }
    /**
     * Admin verifies the payment as legitimate.
     *
     * Optionally corrects the allocations declared by the student before
     * approving — used when the student marked cuotas by mistake (e.g. marked
     * two months but the receipt only covers one). The correction can only
     * REMOVE or REDUCE allocations, never add new installments or raise amounts:
     *   - Every corrected installmentId must exist in the declared allocations.
     *   - Every corrected amount must be `0 < amount <= declaredAmount`.
     *   - The new total must be `> 0` and `<= original amount`.
     * When a correction is applied, `amount` is recomputed and the original
     * declared state is captured in `originalAmount` / `originalAllocations`.
     * Removed / reduced installments are simply left out, so they revert to
     * pending in the derived payment summary.
     */
    verify(adminUid, correctedAllocations) {
        if (this.props.status !== 'pending_verification') {
            throw new Error('Only pending payments can be verified');
        }
        const now = new Date();
        const base = {
            ...this.props,
            status: 'verified',
            verifiedBy: adminUid,
            verifiedAt: now,
            updatedAt: now,
        };
        if (!correctedAllocations) {
            return new TuitionPayment(base);
        }
        // Normalize: drop zero/empty allocations the UI may send for unchecked cuotas.
        const corrected = correctedAllocations.filter((a) => a.amount > 0);
        if (corrected.length === 0) {
            throw new Error('At least one allocation must remain to verify the payment');
        }
        for (const alloc of corrected) {
            const declared = this.props.allocations.find((a) => a.installmentId === alloc.installmentId);
            if (!declared) {
                throw new Error(`Corrected allocation references installment ${alloc.installmentId} not declared in this payment`);
            }
            if (alloc.amount > declared.amount) {
                throw new Error(`Corrected amount for installment ${alloc.installmentId} exceeds the declared amount`);
            }
        }
        const newAmount = corrected.reduce((s, a) => s + a.amount, 0);
        if (newAmount > this.props.amount) {
            throw new Error('Corrected total exceeds the declared payment amount');
        }
        // No effective change → verify as-is, no original snapshot.
        const unchanged = newAmount === this.props.amount &&
            corrected.length === this.props.allocations.length &&
            corrected.every((a) => this.props.allocations.some((d) => d.installmentId === a.installmentId && d.amount === a.amount));
        if (unchanged) {
            return new TuitionPayment(base);
        }
        return new TuitionPayment({
            ...base,
            amount: newAmount,
            allocations: corrected,
            originalAmount: this.props.amount,
            originalAllocations: [...this.props.allocations],
        });
    }
    get adjustmentAmount() {
        return this.props.adjustmentAmount;
    }
    get adjustmentReason() {
        return this.props.adjustmentReason;
    }
    get adjustedBy() {
        return this.props.adjustedBy;
    }
    get adjustedAt() {
        return this.props.adjustedAt;
    }
    get originalAmount() {
        return this.props.originalAmount;
    }
    get originalAllocations() {
        return this.props.originalAllocations ? [...this.props.originalAllocations] : undefined;
    }
    /** Effective amount after any adjustments. Equals `amount` when no adjustment. */
    get effectiveAmount() {
        return this.props.amount;
    }
    /**
     * Admin deducts an amount from a specific allocation of a verified payment.
     * Use case: student overpaid an installment (e.g. paid 30k for a 15k cuota
     * with the surplus applied to next month) and later withdraws. Admin records
     * the deduction with a reason so the surplus is no longer counted as paid.
     *
     * Behavior:
     *   - Only allowed on `verified` payments.
     *   - Reduces `amount` by `deductionAmount`.
     *   - Reduces the target allocation by `deductionAmount` (removes when 0).
     *   - Captures original state on first adjustment (for audit/UI).
     *   - Accumulates `adjustmentAmount` across multiple adjustments.
     *   - Refund handling lives outside the system (informational only).
     */
    adjust(params) {
        if (this.props.status !== 'verified') {
            throw new Error('Only verified payments can be adjusted');
        }
        if (params.deductionAmount <= 0) {
            throw new Error('Deduction amount must be positive');
        }
        if (!params.reason.trim()) {
            throw new Error('Adjustment reason is required');
        }
        const targetIdx = this.props.allocations.findIndex((a) => a.installmentId === params.targetInstallmentId);
        if (targetIdx === -1) {
            throw new Error(`Allocation for installment ${params.targetInstallmentId} not found in this payment`);
        }
        const targetAlloc = this.props.allocations[targetIdx];
        if (params.deductionAmount > targetAlloc.amount) {
            throw new Error('Deduction amount exceeds the allocation amount for the selected installment');
        }
        if (params.deductionAmount > this.props.amount) {
            throw new Error('Deduction amount exceeds the total payment amount');
        }
        const newAllocAmount = targetAlloc.amount - params.deductionAmount;
        const updatedAllocations = newAllocAmount === 0
            ? this.props.allocations.filter((_, i) => i !== targetIdx)
            : this.props.allocations.map((a, i) => i === targetIdx ? { ...a, amount: newAllocAmount } : a);
        const now = new Date();
        const isFirstAdjustment = this.props.adjustmentAmount === undefined;
        const accumulatedAdjustment = (this.props.adjustmentAmount ?? 0) + params.deductionAmount;
        return new TuitionPayment({
            ...this.props,
            amount: this.props.amount - params.deductionAmount,
            allocations: updatedAllocations,
            adjustmentAmount: accumulatedAdjustment,
            adjustmentReason: params.reason.trim(),
            adjustedBy: params.adminUid,
            adjustedAt: now,
            ...(isFirstAdjustment
                ? {
                    originalAmount: this.props.amount,
                    originalAllocations: [...this.props.allocations],
                }
                : {}),
            updatedAt: now,
        });
    }
    /** Admin rejects the payment (e.g. receipt unclear, wrong amount) */
    reject(adminUid, reason) {
        if (this.props.status !== 'pending_verification') {
            throw new Error('Only pending payments can be rejected');
        }
        if (!reason.trim())
            throw new Error('Rejection reason is required');
        return new TuitionPayment({
            ...this.props,
            status: 'rejected',
            verifiedBy: adminUid,
            verifiedAt: new Date(),
            rejectionReason: reason,
            updatedAt: new Date(),
        });
    }
    toSnapshot() {
        return { ...this.props };
    }
}
//# sourceMappingURL=tuition.entity.js.map